import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Shield, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import InlinePlatformNoticeModal from "@/components/InlinePlatformNoticeModal";

export default function Signup() {
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const preselectedPlan = searchParams.get("plan");
    const { signup } = useAuth();
    const [form, setForm] = useState({
        email: "",
        password: "",
        full_name: "",
        company_name: "",
        coupon_code: "",
    });
    const [showCoupon, setShowCoupon] = useState(false);
    const [busy, setBusy] = useState(false);

    // Sprint I-DEFECT-5 — inline Platform Notice modal gate on signup.
    const [modalOpen, setModalOpen] = useState(false);
    // ackState is { version, acknowledged_at } after user clicks "I Acknowledge"
    const [ackState, setAckState]   = useState(null);

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const openModal = () => setModalOpen(true);

    const handleAcknowledge = ({ version, acknowledged_at }) => {
        setAckState({ version, acknowledged_at });
        setModalOpen(false);
        // Persist the ack locally so the authed backfill modal doesn't re-prompt.
        try {
            localStorage.setItem("atlas_platform_notice_ack_version", version);
            localStorage.setItem("atlas_platform_notice_dismissed_at", String(Date.now()));
        } catch {}
        toast.success("Thank you — Platform Notice acknowledged.");
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!ackState) {
            // Open the modal automatically instead of just erroring — this is
            // the required UX (blocks signup until acknowledged).
            setModalOpen(true);
            toast.error("Please read and acknowledge the Nexus One Platform Notice to continue.");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                platform_notice_acknowledged: true,
                platform_notice_version: ackState.version,
            };
            if (!payload.coupon_code?.trim()) delete payload.coupon_code;
            const data = await signup(payload);
            if (data.coupon_applied) {
                toast.success("Welcome, VIP. You're on the Lifetime Free plan.");
            } else {
                toast.success("Welcome to Nexus One");
            }
            // J-2: honor ?plan=SKU deep-link from /pricing → auto-checkout after signup.
            if (preselectedPlan && !data.coupon_applied) {
                nav(`/app/billing?plan=${encodeURIComponent(preselectedPlan)}`, { replace: true });
            } else {
                nav("/app", { replace: true });
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : detail?.message || "Signup failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen wfos-hero-bg flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2 mb-8">
                    <div className="atlas-logo-mark w-7 h-7 text-[11px]">N</div>
                    <span className="text-[15px] font-semibold tracking-tight text-gray-900">Nexus One</span>
                </Link>

                <div className="wfos-card p-7">
                    <h1 className="text-2xl font-medium tracking-tight text-gray-900">Activate your first Workforce Member</h1>
                    <p className="mt-1 text-sm text-gray-500">60 seconds. Starter plan is free.</p>

                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <div>
                            <label className="wfos-label">Your name</label>
                            <input
                                type="text" required value={form.full_name}
                                onChange={upd("full_name")}
                                data-testid="signup-full-name"
                                placeholder="Alex Kim"
                                className="mt-1.5 w-full h-10 rounded-lg border border-[#e8e8e3] bg-white px-3 text-sm focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20"
                            />
                        </div>
                        <div>
                            <label className="wfos-label">Company name</label>
                            <input
                                type="text" required value={form.company_name}
                                onChange={upd("company_name")}
                                data-testid="signup-company-name"
                                placeholder="Acme Corp"
                                className="mt-1.5 w-full h-10 rounded-lg border border-[#e8e8e3] bg-white px-3 text-sm focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20"
                            />
                        </div>
                        <div>
                            <label className="wfos-label">Work email</label>
                            <input
                                type="email" required value={form.email}
                                onChange={upd("email")}
                                data-testid="signup-email"
                                placeholder="alex@acme.com"
                                className="mt-1.5 w-full h-10 rounded-lg border border-[#e8e8e3] bg-white px-3 text-sm focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20"
                            />
                        </div>
                        <div>
                            <label className="wfos-label">Password</label>
                            <input
                                type="password" required minLength={6} value={form.password}
                                onChange={upd("password")}
                                data-testid="signup-password"
                                placeholder="6+ characters"
                                className="mt-1.5 w-full h-10 rounded-lg border border-[#e8e8e3] bg-white px-3 text-sm focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20"
                            />
                        </div>

                        <div className="pt-1">
                            {!showCoupon ? (
                                <button
                                    type="button"
                                    onClick={() => setShowCoupon(true)}
                                    data-testid="signup-vip-toggle"
                                    className="text-xs text-gray-500 hover:text-gray-800"
                                >
                                    Have a VIP code?
                                </button>
                            ) : (
                                <div>
                                    <label className="wfos-label">VIP code (optional)</label>
                                    <input
                                        type="text"
                                        value={form.coupon_code}
                                        onChange={(e) => upd("coupon_code")({ target: { value: e.target.value.toUpperCase() } })}
                                        data-testid="signup-vip-code"
                                        placeholder="WFOS-LIFETIME-XXXXXXXX"
                                        className="mt-1.5 w-full h-10 rounded-lg border border-[#e8e8e3] bg-white px-3 text-sm font-mono focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20"
                                    />
                                    <div className="mt-1 text-[11px] text-gray-500">
                                        Grants Lifetime Free — unlimited Workforce Members, all packs, no billing.
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Platform Notice gate — button opens the inline 25-section modal */}
                        <div className="pt-1" data-testid="signup-legal-gate">
                            {!ackState ? (
                                <button
                                    type="button"
                                    onClick={openModal}
                                    data-testid="signup-open-notice"
                                    className="w-full h-11 rounded-lg inline-flex items-center justify-center gap-2 text-[13px] font-semibold"
                                    style={{
                                        background: "linear-gradient(90deg, rgba(139,92,246,0.14), rgba(6,182,212,0.06))",
                                        color: "var(--atlas-primary-hover)",
                                        border: "1px solid rgba(139,92,246,0.42)",
                                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
                                    }}
                                >
                                    <Shield className="w-3.5 h-3.5" strokeWidth={2} />
                                    Read + Acknowledge the Nexus One Platform Notice
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={openModal}
                                    data-testid="signup-notice-acked"
                                    className="w-full h-11 rounded-lg inline-flex items-center justify-center gap-2 text-[13px] font-medium"
                                    style={{
                                        background: "var(--atlas-success-muted)",
                                        color: "#34d399",
                                        border: "1px solid rgba(16,185,129,0.45)",
                                    }}
                                >
                                    <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                                    Platform Notice v{ackState.version} acknowledged — click to review
                                </button>
                            )}
                            <div className="mt-1.5 text-[10.5px] text-gray-500 leading-relaxed">
                                Required. All 25 sections render inline — no new tab. You'll be blocked from signup until you acknowledge.
                                Also agree to the{" "}
                                <Link to="/legal/terms" className="text-[#16a34a] hover:text-[#15803d] underline">Terms</Link>
                                {" "}and{" "}
                                <Link to="/legal/privacy" className="text-[#16a34a] hover:text-[#15803d] underline">Privacy Policy</Link>.
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={busy || !ackState}
                            data-testid="signup-submit"
                            className="w-full h-10 rounded-lg bg-[#16a34a] text-white text-sm font-medium hover:bg-[#15803d] disabled:opacity-60"
                        >
                            {busy ? "Creating…" : "Create workspace"}
                        </button>
                    </form>

                    <div className="mt-6 text-sm text-gray-500 text-center">
                        Already have a workspace? <Link to="/login" className="text-[#16a34a] hover:text-[#15803d] font-medium">Sign in</Link>
                    </div>
                </div>
            </div>

            <InlinePlatformNoticeModal
                open={modalOpen}
                dismissible={true}
                subtitle="Read the 25 sections below. You cannot create a Nexus One workspace until you acknowledge."
                onDismiss={() => setModalOpen(false)}
                onAcknowledge={handleAcknowledge}
            />
        </div>
    );
}
